/* ===========================================================================
   LMP Shipping — futuristic motion system.
   Scroll-reveal · parallax · 3D tilt · seamless marquee · retailer search ·
   the signature scroll-driven shipping-container journey.
   Transform/opacity only, rAF-batched, fully disabled under reduced-motion.
   =========================================================================== */
(function () {
  'use strict';
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal ----------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.lmp-reveal');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -4% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Parallax + container journey (shared scroll loop) ----------------- */
  var parallaxEls = [];
  var journeys = [];
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;

      parallaxEls.forEach(function (p) {
        var rect = p.el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var off = (center - vh / 2) / vh; // -1..1 around viewport center
        p.el.style.transform = 'translate3d(0,' + (off * p.speed * -100).toFixed(1) + 'px,0)';
      });

      journeys.forEach(function (j) {
        var rect = j.section.getBoundingClientRect();
        // Travel = how long the sticky scene stays pinned = section height minus the
        // scene's own height (NOT the viewport — the scene can be shorter than 100vh).
        var sceneH = j.scene.offsetHeight || vh;
        var total = rect.height - sceneH;
        var scrolled = Math.min(Math.max(-rect.top, 0), total);
        var prog = total > 0 ? scrolled / total : 0; // 0..1 through the section
        j.scene.style.setProperty('--p', prog.toFixed(4));
        var stageIdx = Math.min(j.stages.length - 1, Math.floor(prog * j.stages.length));
        if (stageIdx !== j.active) {
          j.active = stageIdx;
          j.stages.forEach(function (s, i) { s.classList.toggle('is-active', i <= stageIdx); });
        }
      });

      ticking = false;
    });
  }

  function initParallax() {
    if (REDUCED) return;
    document.querySelectorAll('[data-lmp-parallax]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-lmp-parallax')) || 0.15;
      parallaxEls.push({ el: el, speed: speed });
    });
  }

  function initJourney() {
    document.querySelectorAll('[data-lmp-journey]').forEach(function (section) {
      var scene = section.querySelector('[data-lmp-scene]') || section;
      var stages = Array.prototype.slice.call(section.querySelectorAll('[data-lmp-stage]'));
      journeys.push({ section: section, scene: scene, stages: stages, active: -1 });
      if (REDUCED) stages.forEach(function (s) { s.classList.add('is-active'); });
    });
  }

  /* ---- 3D tilt ----------------------------------------------------------- */
  function initTilt() {
    if (REDUCED) return;
    document.querySelectorAll('[data-lmp-tilt]').forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-lmp-tilt')) || 6;
      el.addEventListener('mousemove', function (ev) {
        var r = el.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        el.style.transform = 'perspective(800px) rotateX(' + (-py * max).toFixed(2) + 'deg) rotateY(' + (px * max).toFixed(2) + 'deg)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = 'perspective(800px) rotateX(0) rotateY(0)';
      });
    });
  }

  /* ---- Seamless marquee (duplicate chips inside one track) ----------------
     Duplicate the chips into the SAME track and animate that single element by
     -50% (one set width). One element + one animation = always in phase — no
     overlapping/out-of-sync tracks. */
  function initMarquee() {
    document.querySelectorAll('[data-lmp-marquee]').forEach(function (track) {
      if (track.dataset.lmpCloned) return;
      track.dataset.lmpCloned = '1';
      var chips = Array.prototype.slice.call(track.children);
      chips.forEach(function (chip) {
        var c = chip.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        track.appendChild(c);
      });
      if (REDUCED) track.style.animation = 'none';
    });
  }

  /* ---- Retailer search filter -------------------------------------------- */
  function initSearch() {
    document.querySelectorAll('[data-lmp-search]').forEach(function (input) {
      var scopeSel = input.getAttribute('data-lmp-search');
      var scope = scopeSel ? document.querySelector(scopeSel) : document;
      if (!scope) return;
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var anyVisibleGroup = {};
        scope.querySelectorAll('[data-lmp-store]').forEach(function (card) {
          var name = (card.getAttribute('data-lmp-store') || card.textContent).toLowerCase();
          var show = !q || name.indexOf(q) !== -1;
          card.style.display = show ? '' : 'none';
          if (show) anyVisibleGroup[card.getAttribute('data-lmp-group')] = true;
        });
        scope.querySelectorAll('[data-lmp-group-section]').forEach(function (sec) {
          var g = sec.getAttribute('data-lmp-group-section');
          sec.style.display = !q || anyVisibleGroup[g] ? '' : 'none';
        });
      });
    });
  }

  function init() {
    initReveal();
    initParallax();
    initJourney();
    initTilt();
    initMarquee();
    initSearch();
    if ((parallaxEls.length || journeys.length) && !REDUCED) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
